import React, { useRef, useState } from 'react';
import {
  useParticularTravel,
  columnsLabels,
  getDefaultFilters
} from '../hook/useParticularTravel';
import PageHeader from 'layout/components/PageHeader';
import PageContainer from 'layout/components/PageContainer';
import ParticularTravelsTable from '../components/particular-travels-table/ParticularTravelsTable';
import { createTableScrollHandler } from 'utils/tableUtils';
import { Divider, Drawer } from '@material-ui/core';
import ManualPaymentModal from '../components/ManualPaymentModal';
import LoadBillingsModal from '../components/particular-travels-table/load-billings-modal/LoadBillingsModal';
import { ViajeTrasladoTipoServicio } from 'app/viaje/models';
import { TransferReservationType } from '../../viajeTraslado/models/TransferReservationPartial';
import ParticularTravelsFilters from '../components/particular-travels-filters/ParticularTravelsFilters';
import RefreshList from 'shared/components/Table/Refresh';
import ColumnSelector from 'shared/components/Table/actions-buttons/ColumnSelector';
import FiltersButton from 'shared/components/Table/FiltersButton';
import TableToolbar from 'shared/components/Table/TableToolbar';
import CloseFiltersButton from 'shared/components/Table/CloseFiltersButton';
import TrashAllFilters from 'shared/components/filters/TrashAllFilters';
import ChipsBar from '../components/particular-travels-filters/ChipsBar';
import { Button } from '@vadiun/react-components';
import { FontAwesomeIcon, } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import DownloadExcelModal from 'shared/components/userApp/DownLoadExcelModal';
import { particularTravelService } from '../service/particularTravelService';

const AppPaymentsPage = () => {
  const tableRef = useRef<HTMLDivElement>(null);
  const {
    particularTravels,
    filters,
    onFiltersChange,
    reload,
    selectedIds,
    setSelectedIds,
    onManualPayment,
    onBill,
    onSendBilling,
    columnVisibility,
    setColumnVisibility,
    onChangePage,
    onChangeRowsPerPage,
    onSortChange,
  } = useParticularTravel();

  const ParticularTravelService = particularTravelService();
  const [showFilters, setShowFilters] = useState(false);
  const [selectedClientLabel, setSelectedClientLabel] = useState('');
  const [manualPaymentModal, setManualPaymentModal] = useState<{
    isOpen: boolean;
    travelId: number | null;
    amount: number | null;
  }>({ isOpen: false, travelId: null, amount: null });
  const [billModal, setBillModal] = useState<{
    isOpen: boolean;
  }>({ isOpen: false });
  const [openDownloadExcel, setOpenDownloadExcel] = useState(false);

  const selectedGroups = (particularTravels.data ?? []).reduce(
    (
      acc: {
        clientId: number;
        clientName: string;
        companyName?: string;
        service: ViajeTrasladoTipoServicio;
        type: TransferReservationType;
        ids: number[];
      }[],
      travel
    ) => {
      if (!selectedIds.includes(travel.id)) return acc;
      const existing = acc.find((g) => g.clientId === travel.client.id);
      if (existing) {
        existing.ids.push(travel.id);
      } else {
        acc.push({
          clientId: travel.client.id,
          clientName: travel.client.name,
          companyName: travel.client.company_name,
          service: travel.service,
          type: travel.type,
          ids: [travel.id]
        });
      }
      return acc;
    },
    []
  );

  const selectedTravels = (particularTravels.data ?? [])
    .filter((travel) => selectedIds.includes(travel.id))
    .map((travel) => ({
      id: travel.id,
      amount: Number(travel.payment_info.amount)
    }));
  const handleCloseManualPayment = () => {
    setManualPaymentModal({ isOpen: false, travelId: null, amount: null });
  };

  const handleManualPayment = async (
    travelId: number,
    payment_method_id: number,
    transaction_number: string | null,
    notes: string | null
  ) => {
    try {
      await onManualPayment.mutate(
        travelId,
        payment_method_id,
        transaction_number,
        notes
      );
    } catch (error) {
      console.error('Error al guardar pago manual:', error);
    } finally {
      reload();
    }
  };

  const handleBill = async (number: string, letter: string) => {
    const idsToSend = [...selectedIds];
    try {
      await onBill.mutate(number.toString(), letter);
      await onSendBilling.mutate(idsToSend);
      setSelectedIds([]);
    } catch (error) {
      console.error('Error al facturar y enviar:', error);
    } finally {
      reload();
    }
  };

  const handleDownloadExcel = async (values: any) => {
    try {
      const response = await (ParticularTravelService.downloadExcel(values) as unknown as Promise<{ data: number[] }>);

      if (!response || !response.data) {
        console.error("La respuesta no contiene datos");
        return;
      }

      const byteArray = new Uint8Array(response.data);

      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `informe-pagos-app.xlsx`);
      document.body.appendChild(link);
      link.click();

      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      setOpenDownloadExcel(false);
    } catch (error) {
      console.error('Error al descargar excel:', error);
    }
  }

  return (
    <div ref={tableRef}>
      <PageHeader
        area="Administración"
        title="Cobros App"
        subArea='Listado de viajes particulares a cobrar y facturar'
        toolbar={<Button onClick={() => setOpenDownloadExcel(true)} variant="outlined">
          <div className="flex gap-x-2 items-center">
            <FontAwesomeIcon icon={faDownload} />
            Descargar Informe
          </div>
        </Button>}
      />
      <div
        className="sticky top-0 right-0 left-0 flex flex-col "
        style={{
          zIndex: 101,
          background: '#EAEDF5',
          // border: '1px solid rgba( 255, 255, 255, 0.18 )',
          marginBottom: '-24px'
        }}
      >
        <div className="flex w-full px-8 mt-2">
          <TableToolbar
            chips={
              <ChipsBar
                filters={filters}
                defaultFilters={getDefaultFilters()}
                onFiltersChange={onFiltersChange}
                selectedClientLabel={selectedClientLabel}
              />
            }
            actions={
              <>
                <RefreshList
                  onRefreshTable={() => reload()}
                  isRefreshing={particularTravels.isLoading}
                />
                <ColumnSelector
                  columns={columnVisibility}
                  onChange={setColumnVisibility}
                  columnLabels={columnsLabels}
                />
                <FiltersButton
                  onClick={() => setShowFilters(true)}
                  isActive={showFilters}
                />
              </>
            }
          />
        </div>
      </div>
      <PageContainer lessMargin>
        <ParticularTravelsTable
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          handlePageChange={createTableScrollHandler(tableRef)}
          particularTravels={particularTravels}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          onSortChange={onSortChange}
          filters={filters}
          onFiltersChange={onFiltersChange}
          onManualPayment={(id, amount) => {
            setManualPaymentModal({
              isOpen: true,
              travelId: id,
              amount: amount
            });
          }}
          selectedClientLabel={selectedClientLabel}
          onManualBilling={() => {
            setBillModal({ isOpen: true });
          }}
          columnVisibility={columnVisibility}
        />
      </PageContainer>
      <Drawer
        variant="persistent"
        anchor="right"
        open={showFilters}
        className="max-w-md"
        transitionDuration={300}
      >
        <div className="flex flex-col h-full">
          <div
            className="flex justify-between items-center px-2"
            style={{ backgroundColor: '#00163B' }}
          >
            <CloseFiltersButton
              onCloseFilters={() => setShowFilters(false)}
              inOpen={showFilters}
            />
            <TrashAllFilters
              onClick={() => {
                onFiltersChange(getDefaultFilters());
                setSelectedIds([]);
                setSelectedClientLabel('');
              }}
            />
          </div>
          <Divider />
          <ParticularTravelsFilters
            onSubmit={async (filters) => {
              onFiltersChange(filters);
              setShowFilters(false);
            }}
            initialValues={filters}
            setSelectedClientLabel={setSelectedClientLabel}
          />
        </div>
      </Drawer>
      {manualPaymentModal.isOpen && (
        <ManualPaymentModal
          travelId={manualPaymentModal.travelId!}
          isOpen={manualPaymentModal.isOpen}
          amount={manualPaymentModal.amount!}
          onClose={handleCloseManualPayment}
          handleManualPayment={handleManualPayment}
        />
      )}
      {billModal.isOpen && (
        <LoadBillingsModal
          isOpen={billModal.isOpen}
          onClose={() => setBillModal({ isOpen: false })}
          handleBill={handleBill}
          selectedGroups={selectedGroups}
          selectedTravels={selectedTravels}
        />
      )}
      {openDownloadExcel && (
        <DownloadExcelModal
          isOpen={openDownloadExcel}
          onClose={() => setOpenDownloadExcel(false)}
          onSubmit={handleDownloadExcel}
        />
      )}
    </div>
  );
};

export default AppPaymentsPage;
